import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, fileDownloadUrl } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { uploadFile } from "@/components/UploadButton";
import { Mail, Plus, Sparkles, Send, ArrowLeft, Ticket, InboxIcon, Zap, RefreshCw, PenSquare, Trash2, RotateCcw, X, Bold, Italic, Link2, Paperclip, Smile, FileText, LayoutTemplate, Loader2 } from "lucide-react";
import { toast } from "sonner";

const URGENCY = { low: "bg-slate-500/15 text-slate-600", medium: "bg-blue-500/15 text-blue-600",
  high: "bg-amber-500/15 text-amber-600", critical: "bg-destructive/15 text-destructive" };
const EMOJIS = ["😀", "😊", "👍", "🙏", "✅", "⚠️", "📌", "📅", "💬", "❤️", "🎉", "🔔", "📎", "⭐", "😅", "🙂"];
const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
const dayLabel = (iso, lang) => new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { day: "numeric", month: "short", year: "numeric" });

// ---------- Attachment chip ----------
function AttChip({ att, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg bg-accent text-xs" data-testid="composer-attachment">
      <FileText className="w-3.5 h-3.5" />
      <span className="max-w-[140px] truncate">{att.name}</span>
      {onRemove ? (
        <button onClick={onRemove} className="hover:text-destructive"><X className="w-3 h-3" /></button>
      ) : (
        <a href={fileDownloadUrl(att.file_id)} target="_blank" rel="noreferrer" className="text-primary text-[10px] underline">↓</a>
      )}
    </span>
  );
}

// ---------- Shared rich composer ----------
function MailComposer({ mode, accounts, templates, defaultAccountId, headerTo, sending, onSend, onSaveTemplate }) {
  const { t } = useT();
  const [accountId, setAccountId] = useState(defaultAccountId || accounts[0]?.id || "");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [atts, setAtts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const ref = useRef();
  const fileRef = useRef();

  const reset = () => { setTo(""); setSubject(""); setBody(""); setCc(""); setBcc(""); setShowCc(false); setShowBcc(false); setAtts([]); };

  const surround = (b, a = b) => {
    const el = ref.current; const s = el ? el.selectionStart : body.length; const e = el ? el.selectionEnd : body.length;
    setBody(body.slice(0, s) + b + body.slice(s, e) + a + body.slice(e));
  };
  const insert = (txt) => {
    const el = ref.current; const p = el ? el.selectionStart : body.length;
    setBody(body.slice(0, p) + txt + body.slice(p));
  };
  const addLink = () => { const url = window.prompt(t("linkUrl")); if (url) insert(url.startsWith("http") ? url : `https://${url}`); };
  const pickFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const d = await uploadFile(file, "email"); setAtts((a) => [...a, { file_id: d.id, name: d.original_filename, content_type: d.content_type }]); }
    catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const applyTemplate = (tpl) => { insert(tpl.body); if (mode === "compose" && !subject && tpl.subject) setSubject(tpl.subject); };
  const saveTpl = () => { const name = window.prompt(t("templateName")); if (name) onSaveTemplate({ name, subject: mode === "compose" ? subject : "", body }); };

  const submit = async () => {
    const payload = { account_id: accountId, to_email: to, subject, body, cc, bcc, attachments: atts };
    const ok = await onSend(payload);
    if (ok) reset();
  };
  const canSend = mode === "compose" ? (accountId && to.trim() && subject.trim()) : body.trim().length > 0;

  return (
    <div className="space-y-2" data-testid={`composer-${mode}`}>
      {mode === "compose" ? (
        <>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-12 text-muted-foreground shrink-0">{t("fromEmail")}</span>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="h-9" data-testid="composer-account"><SelectValue placeholder={t("emailAddress")} /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.address}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <FieldRow label={t("to")}><Input className="h-9 border-0 shadow-none px-0 focus-visible:ring-0" value={to} onChange={(e) => setTo(e.target.value)} data-testid="composer-to" /></FieldRow>
        </>
      ) : (
        <div className="text-[11px] text-muted-foreground">{t("to")}: {headerTo}</div>
      )}

      {showCc && <FieldRow label={t("cc")}><Input className="h-9 border-0 shadow-none px-0 focus-visible:ring-0" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="composer-cc" /></FieldRow>}
      {showBcc && <FieldRow label={t("bcc")}><Input className="h-9 border-0 shadow-none px-0 focus-visible:ring-0" value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="composer-bcc" /></FieldRow>}
      {mode === "compose" && <FieldRow label={t("subject")}><Input className="h-9 border-0 shadow-none px-0 focus-visible:ring-0 font-medium" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="composer-subject" /></FieldRow>}

      <Textarea ref={ref} rows={mode === "compose" ? 7 : 4} value={body} onChange={(e) => setBody(e.target.value)}
        placeholder={mode === "compose" ? t("message") : t("replyHere")} className="resize-none" data-testid={`composer-body-${mode}`} />

      {atts.length > 0 && <div className="flex flex-wrap gap-1.5">{atts.map((a, i) => <AttChip key={`${a.file_id}-${i}`} att={a} onRemove={() => setAtts(atts.filter((_, x) => x !== i))} />)}</div>}

      <div className={`flex items-center gap-0.5 flex-wrap ${mode === "reply" ? "pr-16 sm:pr-20" : ""}`}>
        <TB onClick={() => surround("**")} title="Bold"><Bold className="w-4 h-4" /></TB>
        <TB onClick={() => surround("_")} title="Italic"><Italic className="w-4 h-4" /></TB>
        <TB onClick={addLink} title={t("insertLink")}><Link2 className="w-4 h-4" /></TB>
        <Popover>
          <PopoverTrigger asChild><TB title="Emoji" testId="composer-emoji"><Smile className="w-4 h-4" /></TB></PopoverTrigger>
          <PopoverContent className="w-56 p-2"><div className="grid grid-cols-8 gap-1">{EMOJIS.map((em) => <button key={em} onClick={() => insert(em)} className="text-lg hover:bg-accent rounded p-1">{em}</button>)}</div></PopoverContent>
        </Popover>
        <TB onClick={() => fileRef.current?.click()} title={t("attachFile")} testId="composer-attach">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}</TB>
        <input ref={fileRef} type="file" className="hidden" onChange={pickFile} />
        <Popover>
          <PopoverTrigger asChild><TB title={t("templates")} testId="composer-templates"><LayoutTemplate className="w-4 h-4" /></TB></PopoverTrigger>
          <PopoverContent className="w-64 p-1">
            {templates.length === 0 ? <p className="text-xs text-muted-foreground p-2">{t("noTemplates")}</p> :
              templates.map((tp) => <button key={tp.id} onClick={() => applyTemplate(tp)} className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent truncate" data-testid="template-option">{tp.name}</button>)}
            {body.trim() && <button onClick={saveTpl} className="w-full text-left text-xs text-primary px-2 py-1.5 rounded hover:bg-accent border-t border-border mt-1" data-testid="save-template">+ {t("saveAsTemplate")}</button>}
          </PopoverContent>
        </Popover>
        {!showCc && <button onClick={() => setShowCc(true)} className="text-xs text-muted-foreground px-1.5 hover:text-foreground" data-testid="toggle-cc">{t("cc")}</button>}
        {!showBcc && <button onClick={() => setShowBcc(true)} className="text-xs text-muted-foreground px-1.5 hover:text-foreground" data-testid="toggle-bcc">{t("bcc")}</button>}
        <div className="flex-1" />
        <Button size="sm" onClick={submit} disabled={sending || !canSend} data-testid={`send-${mode}`}><Send className="w-4 h-4 mr-1.5" />{t("send")}</Button>
      </div>
    </div>
  );
}

const FieldRow = ({ label, children }) => (
  <div className="flex items-center gap-2 border-b border-border/60">
    <span className="w-12 text-sm text-muted-foreground shrink-0">{label}</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);
const TB = React.forwardRef(({ onClick, title, testId, children }, ref) => (
  <button ref={ref} type="button" onClick={onClick} title={title} data-testid={testId}
    className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">{children}</button>
));

export default function Inbox() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const { role, isSuperAdmin } = useCenter();
  const isAdmin = isSuperAdmin || role === "admin";
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [thread, setThread] = useState(null);
  const [active, setActive] = useState(null);
  const [folder, setFolder] = useState("inbox");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [newAddr, setNewAddr] = useState("");
  const [smtp, setSmtp] = useState({ provider_preset: "gmail", address: "", password: "", display_name: "", imap_host: "", imap_port: "", smtp_host: "", smtp_port: "", smtp_security: "ssl" });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [sim, setSim] = useState({ account_id: "", from_name: "", from_email: "", subject: "", body: "" });
  const [simLoading, setSimLoading] = useState(false);
  const [composeLoading, setComposeLoading] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [converting, setConverting] = useState(false);
  const hasGmail = accounts.some((a) => a.provider === "gmail");
  const hasSmtp = accounts.some((a) => a.provider === "smtp");
  const hasOutlook = accounts.some((a) => a.provider === "outlook");
  const canSync = hasGmail || hasSmtp || hasOutlook;

  const loadAccounts = () => api.get("/email/accounts").then((r) => setAccounts(r.data));
  const loadTemplates = () => api.get("/email/templates").then((r) => setTemplates(r.data)).catch(() => {});
  const loadMessages = (f = folder) => api.get("/email/messages", { params: { folder: f } }).then((r) => setMessages(r.data));
  useEffect(() => { loadAccounts(); loadTemplates(); }, []);
  useEffect(() => { setActive(null); setThread(null); loadMessages(folder); }, [folder]); // eslint-disable-line

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gmail");
    if (g === "connected") { toast.success(t("gmailConnected")); loadAccounts(); setTimeout(() => syncGmail(), 400); window.history.replaceState({}, "", window.location.pathname); }
    else if (g === "error") { toast.error(t("gmailError")); window.history.replaceState({}, "", window.location.pathname); }
    const o = params.get("outlook");
    if (o === "connected") { toast.success(t("outlookConnected")); loadAccounts(); setTimeout(() => syncGmail(), 400); window.history.replaceState({}, "", window.location.pathname); }
    else if (o === "error") { toast.error(t("outlookError")); window.history.replaceState({}, "", window.location.pathname); }
  }, []); // eslint-disable-line

  const connectGmail = async () => {
    try { const { data } = await api.get("/email/gmail/auth-url"); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const connectOutlook = async () => {
    try { const { data } = await api.get("/email/outlook/auth-url"); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const syncGmail = async () => {
    setSyncing(true);
    try {
      let total = 0;
      if (hasGmail) { const { data } = await api.post("/email/gmail/sync"); total += data.synced; }
      if (hasSmtp) { const { data } = await api.post("/email/smtp/sync"); total += data.synced; }
      if (hasOutlook) { const { data } = await api.post("/email/outlook/sync"); total += data.synced; }
      toast.success(`${total} ${t("synced")}`); loadMessages(); setTimeout(() => loadMessages(), 6000);
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };
  const connectSmtp = async () => {
    if (!smtp.address.trim() || !smtp.password.trim()) { toast.error(t("smtpFillRequired")); return; }
    setSmtpLoading(true);
    try {
      const body = { address: smtp.address.trim(), password: smtp.password, provider_preset: smtp.provider_preset,
        display_name: smtp.display_name || null };
      if (smtp.provider_preset === "custom") {
        Object.assign(body, { imap_host: smtp.imap_host, imap_port: Number(smtp.imap_port) || null,
          smtp_host: smtp.smtp_host, smtp_port: Number(smtp.smtp_port) || null, smtp_security: smtp.smtp_security });
      }
      await api.post("/email/smtp/connect", body);
      toast.success(t("emailConnected")); setConnectOpen(false);
      setSmtp({ provider_preset: "gmail", address: "", password: "", display_name: "", imap_host: "", imap_port: "", smtp_host: "", smtp_port: "", smtp_security: "ssl" });
      loadAccounts(); setTimeout(() => syncGmail(), 500);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSmtpLoading(false); }
  };
  const removeAccount = async (a) => {
    if (!window.confirm(t("removeAccountConfirm"))) return;
    try { await api.delete(`/email/accounts/${a.id}`); toast.success(t("disconnect")); loadAccounts(); loadMessages(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openThread = async (m) => {
    setActive(m);
    const { data } = await api.get(`/email/threads/${m.thread_id}`); setThread(data);
    if (m.status === "unread") { api.post(`/email/messages/${m.id}/read`); loadMessages(); }
  };
  const connect = async () => {
    try { await api.post("/email/accounts", { address: newAddr }); toast.success(t("connectEmail")); setNewAddr(""); setConnectOpen(false); loadAccounts(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runSim = async () => {
    setSimLoading(true);
    try { await api.post("/email/inbound", sim); toast.success(t("aiTriage") + " ✓"); setSimOpen(false); setSim({ account_id: "", from_name: "", from_email: "", subject: "", body: "" }); setFolder("inbox"); loadMessages("inbox"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSimLoading(false); }
  };
  const saveTemplate = async (tpl) => {
    try { await api.post("/email/templates", tpl); toast.success(t("saveAsTemplate")); loadTemplates(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendReply = async (payload) => {
    setReplyLoading(true);
    try {
      await api.post(`/email/messages/${active.id}/reply`, { body: payload.body, cc: payload.cc, bcc: payload.bcc, attachments: payload.attachments });
      const { data } = await api.get(`/email/threads/${active.thread_id}`); setThread(data); toast.success(t("send")); return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setReplyLoading(false); }
  };
  const sendCompose = async (payload) => {
    setComposeLoading(true);
    try {
      await api.post("/email/compose", payload); toast.success(t("send")); setComposeOpen(false);
      if (folder === "sent") loadMessages("sent"); else setFolder("sent"); return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setComposeLoading(false); }
  };
  const trashThread = async () => {
    try { await api.post(`/email/threads/${active.thread_id}/trash`); toast.success(t("delete")); setActive(null); setThread(null); loadMessages(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const restoreThread = async () => {
    try { await api.post(`/email/threads/${active.thread_id}/restore`); toast.success(t("restore")); setActive(null); setThread(null); loadMessages(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deletePermanent = async () => {
    if (!window.confirm(`${t("deletePermanently")} ?`)) return;
    try { await api.delete(`/email/threads/${active.thread_id}`); setActive(null); setThread(null); loadMessages(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const convertToTicket = async () => {
    setConverting(true);
    try { const { data } = await api.post(`/email/messages/${active.id}/convert`); toast.success(`${t("ticketCreated")} ${data.ticket_number}`); setActive((a) => ({ ...a, ticket_id: data.ticket_id, ticket_number: data.ticket_number })); loadMessages(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setConverting(false); }
  };

  const FOLDERS = [
    { id: "inbox", label: t("inboxShort"), icon: InboxIcon },
    { id: "sent", label: t("sent"), icon: Send },
    { id: "trash", label: t("trash"), icon: Trash2 },
  ];
  const shown = messages.filter((m) => !unreadOnly || m.status === "unread");

  // group thread by date
  const groups = [];
  (thread || []).forEach((msg) => {
    const d = dayLabel(msg.created_at, lang);
    const last = groups[groups.length - 1];
    if (last && last.day === d) last.items.push(msg); else groups.push({ day: d, items: [msg] });
  });

  return (
    <div className="h-full flex overflow-hidden">
      {/* list */}
      <div className={`w-full sm:w-[27rem] border-r border-border flex flex-col bg-card ${active ? "hidden sm:flex" : "flex"}`}>
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <h1 className="font-display text-xl font-bold flex items-center gap-2"><InboxIcon className="w-5 h-5 text-primary" />{t("emailCrm")}</h1>
          <div className="flex gap-1">
            {canSync && <Button size="icon" variant="ghost" onClick={syncGmail} disabled={syncing} title={t("syncGmail")} data-testid="sync-gmail-button"><RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /></Button>}
            <Button size="icon" variant="ghost" onClick={() => { setSim((s) => ({ ...s, account_id: accounts[0]?.id || "" })); setSimOpen(true); }} title={t("simulateEmail")} data-testid="simulate-email-button"><Zap className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setConnectOpen(true)} title={t("connectEmail")} data-testid="connect-email-button"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Filter toggle + compose */}
        <div className="px-4 pb-3 flex items-center justify-between gap-2">
          <div className="inline-flex items-center rounded-full bg-muted p-0.5">
            {[{ id: false, label: t("allMail") }, { id: true, label: t("unread") }].map((f) => (
              <button key={String(f.id)} onClick={() => setUnreadOnly(f.id)} data-testid={`filter-${f.id ? "unread" : "all"}`}
                className={`text-xs px-3.5 py-1.5 rounded-full font-medium transition-colors ${unreadOnly === f.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{f.label}</button>
            ))}
          </div>
          <Button size="sm" onClick={() => setComposeOpen(true)} disabled={accounts.length === 0} className="rounded-full h-8" data-testid="compose-button"><PenSquare className="w-3.5 h-3.5 mr-1.5" />{t("newEmail")}</Button>
        </div>

        {/* Clean underline folder tabs (icon on top) */}
        <div className="px-3 flex border-b border-border">
          {FOLDERS.map((f) => (
            <button key={f.id} onClick={() => setFolder(f.id)} data-testid={`folder-${f.id}`}
              className={`flex-1 flex flex-col items-center gap-1 pt-1 pb-2 relative transition-colors ${folder === f.id ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              <f.icon className="w-[18px] h-[18px]" strokeWidth={folder === f.id ? 2.4 : 2} />
              <span className="text-xs font-medium">{f.label}</span>
              {folder === f.id && <span className="absolute -bottom-px left-4 right-4 h-0.5 bg-primary rounded-full" />}
            </button>
          ))}
        </div>

        {/* Connected accounts (with remove for admins) */}
        {accounts.length > 0 && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {accounts.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-accent text-[11px]" data-testid="account-chip">
                <Mail className="w-3 h-3" />{a.address}
                {a.provider === "gmail" && <span className="text-[9px] text-primary font-semibold">G</span>}
                {a.provider === "outlook" && <span className="text-[9px] text-[#0072C6] font-semibold">O</span>}
                {a.provider === "smtp" && <span className="text-[9px] text-emerald-600 font-semibold">IMAP</span>}
                {isAdmin && <button onClick={() => removeAccount(a)} title={t("disconnect")} data-testid="remove-account-button" className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-destructive/15 hover:text-destructive"><X className="w-3 h-3" /></button>}
              </span>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {shown.length === 0 && <p className="text-center text-muted-foreground py-10 text-sm">{t("noEmails")}</p>}
          {shown.map((m) => (
            <button key={m.id} onClick={() => openThread(m)} data-testid="email-item"
              className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-accent/60 transition-colors ${m.status === "unread" ? "bg-primary/5" : ""} ${active?.id === m.id ? "bg-accent" : ""}`}>
              <div className="flex items-start gap-3">
                {m.status === "unread" && <span className="mt-2 w-2 h-2 rounded-full bg-primary shrink-0" />}
                <div className={`w-9 h-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold ${m.status === "unread" ? "" : "ml-5"}`}>{initials(m.from_name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm flex-1 truncate ${m.status === "unread" ? "font-bold" : "font-medium"}`}>{folder === "sent" ? `${t("to")}: ${m.to_email}` : m.from_name}</p>
                    <span className="text-[10px] text-muted-foreground shrink-0">{dayLabel(m.created_at, lang)}</span>
                  </div>
                  <p className="text-sm truncate">{m.subject || "(no subject)"}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.ai?.summary || m.snippet}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {m.ai && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${URGENCY[m.ai.urgency]}`}>{t(m.ai.urgency)}</span>}
                    {m.ai && <Badge variant="outline" className="text-[9px]">{m.ai.category}</Badge>}
                    {m.ticket_id && <Badge className="text-[9px] bg-emerald-500/15 text-emerald-600"><Ticket className="w-2.5 h-2.5 mr-0.5" />{t("autoTicket")}</Badge>}
                    {m.thread_count > 1 && <Badge variant="secondary" className="text-[9px]">{m.thread_count} {t("threadMessages")}</Badge>}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* thread */}
      <div className={`flex-1 flex flex-col bg-background min-w-0 ${active ? "flex" : "hidden sm:flex"}`}>
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground"><Mail className="w-12 h-12 mb-3 opacity-40" /><p>{t("noConversation")}</p></div>
        ) : (
          <>
            <div className="min-h-14 border-b border-border flex items-center gap-2 px-4 py-2 flex-wrap">
              <button className="sm:hidden" onClick={() => setActive(null)}><ArrowLeft className="w-5 h-5" /></button>
              <p className="font-display font-semibold text-lg truncate flex-1 min-w-0">{active.subject || "(no subject)"}</p>
              {folder !== "trash" && (active.ticket_id ? (
                <Button size="sm" variant="outline" onClick={() => navigate(`/tickets?open=${active.ticket_id}`)} data-testid="view-ticket-button"><Ticket className="w-4 h-4 mr-1.5" />{active.ticket_number || t("viewTicket")}</Button>
              ) : (
                <Button size="sm" variant="outline" onClick={convertToTicket} disabled={converting} data-testid="convert-ticket-button">{converting ? <Sparkles className="w-4 h-4 mr-1.5 animate-pulse" /> : <Ticket className="w-4 h-4 mr-1.5" />}{t("convertToTicket")}</Button>
              ))}
              {folder === "trash" ? (
                <>
                  <Button size="sm" variant="outline" onClick={restoreThread} data-testid="restore-email-button"><RotateCcw className="w-4 h-4 mr-1.5" />{t("restore")}</Button>
                  <Button size="sm" variant="destructive" onClick={deletePermanent} data-testid="delete-permanent-button"><Trash2 className="w-4 h-4 mr-1.5" />{t("deletePermanently")}</Button>
                </>
              ) : (
                <Button size="icon" variant="ghost" onClick={trashThread} title={t("delete")} data-testid="delete-email-button"><Trash2 className="w-4 h-4 text-destructive" /></Button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin p-4 sm:p-6 space-y-4">
              {active.ai && (
                <Card className="p-4 border-primary/30 bg-gradient-to-br from-primary/5 to-purple-500/5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap"><Sparkles className="w-4 h-4 text-primary" /><span className="font-semibold text-sm">{t("aiTriage")}</span>
                    {active.ai.urgency && <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${URGENCY[active.ai.urgency]}`}>{t(active.ai.urgency)}</span>}
                    <Badge variant="outline" className="text-[10px]">{active.ai.category}</Badge>
                  </div>
                  <p className="text-sm"><b>{t("aiSummary")}:</b> {active.ai.summary}</p>
                  {active.ai.follow_ups?.length > 0 && (
                    <div className="mt-2"><p className="text-xs font-semibold text-muted-foreground">{t("followUps")}:</p>
                      <ul className="text-sm list-disc ml-5">{active.ai.follow_ups.map((f, i) => <li key={i}>{f}</li>)}</ul></div>
                  )}
                </Card>
              )}
              {groups.map((g) => (
                <div key={g.day} className="space-y-4">
                  <div className="flex items-center gap-3"><div className="flex-1 border-t border-border" /><span className="text-[11px] font-medium text-muted-foreground bg-accent px-2.5 py-0.5 rounded-full">{g.day}</span><div className="flex-1 border-t border-border" /></div>
                  {g.items.map((msg) => (
                    <div key={msg.id} className={`rounded-xl border shadow-sm overflow-hidden ${msg.direction === "outbound" ? "bg-primary/[0.04] border-primary/20 sm:ml-8" : "bg-card border-border sm:mr-8"}`} data-testid="email-message">
                      <div className="flex items-center gap-3 px-4 pt-3 pb-2 border-b border-border/60">
                        <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${msg.direction === "outbound" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>{initials(msg.from_name)}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold truncate">{msg.from_name} <span className="text-muted-foreground font-normal">&lt;{msg.from_email}&gt;</span></p>
                          <p className="text-[11px] text-muted-foreground truncate">{t("to")}: {msg.to_email}{msg.cc ? ` · ${t("cc")}: ${msg.cc}` : ""} · {new Date(msg.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <p className="px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-w-full text-foreground">{msg.body}</p>
                      {msg.attachments?.length > 0 && <div className="px-4 pb-3 flex flex-wrap gap-1.5">{msg.attachments.map((a, i) => <AttChip key={a.file_id || i} att={a} />)}</div>}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {folder !== "trash" && (
              <div className="p-3 sm:p-4 border-t border-border bg-card">
                <MailComposer mode="reply" accounts={accounts} templates={templates} headerTo={active.from_email}
                  sending={replyLoading} onSend={sendReply} onSaveTemplate={saveTemplate} />
              </div>
            )}
          </>
        )}
      </div>

      {/* compose dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PenSquare className="w-5 h-5 text-primary" />{t("newEmail")}</DialogTitle>
            <DialogDescription>{t("compose")}</DialogDescription>
          </DialogHeader>
          <MailComposer mode="compose" accounts={accounts} templates={templates} defaultAccountId={accounts[0]?.id}
            sending={composeLoading} onSend={sendCompose} onSaveTemplate={saveTemplate} />
        </DialogContent>
      </Dialog>

      {/* connect dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle>{t("connectEmail")}</DialogTitle>
            <DialogDescription>{t("smtpInfo")}</DialogDescription>
          </DialogHeader>

          {/* SMTP / IMAP — any provider */}
          <div className="space-y-2.5">
            <Button onClick={connectGmail} variant="outline" className="w-full" data-testid="connect-gmail-button"><Mail className="w-4 h-4 mr-2 text-[#EA4335]" />{t("connectGmail")} (OAuth)</Button>
            <Button onClick={connectOutlook} variant="outline" className="w-full" data-testid="connect-outlook-button"><Mail className="w-4 h-4 mr-2 text-[#0072C6]" />{t("connectOutlook")} (OAuth)</Button>
            <div className="relative py-1"><div className="border-t border-border" /><span className="absolute left-1/2 -translate-x-1/2 -top-2 bg-background px-2 text-[10px] text-muted-foreground uppercase">{t("orConnectAny")}</span></div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">{t("emailProvider")}</label>
              <Select value={smtp.provider_preset} onValueChange={(v) => setSmtp((s) => ({ ...s, provider_preset: v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="smtp-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gmail">Gmail</SelectItem>
                  <SelectItem value="outlook">Outlook / Office 365</SelectItem>
                  <SelectItem value="yahoo">Yahoo</SelectItem>
                  <SelectItem value="icloud">iCloud</SelectItem>
                  <SelectItem value="custom">{t("customServer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input placeholder={t("emailAddress")} value={smtp.address} onChange={(e) => setSmtp((s) => ({ ...s, address: e.target.value }))} data-testid="smtp-address" />
            <div>
              <Input type="password" placeholder={t("appPassword")} value={smtp.password} onChange={(e) => setSmtp((s) => ({ ...s, password: e.target.value }))} data-testid="smtp-password" />
              {smtp.provider_preset !== "custom" && <p className="text-[10px] text-muted-foreground mt-1">{t("appPasswordHint")}</p>}
            </div>
            {smtp.provider_preset === "custom" && (
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="IMAP host" value={smtp.imap_host} onChange={(e) => setSmtp((s) => ({ ...s, imap_host: e.target.value }))} data-testid="smtp-imap-host" />
                <Input placeholder="IMAP port" value={smtp.imap_port} onChange={(e) => setSmtp((s) => ({ ...s, imap_port: e.target.value }))} data-testid="smtp-imap-port" />
                <Input placeholder="SMTP host" value={smtp.smtp_host} onChange={(e) => setSmtp((s) => ({ ...s, smtp_host: e.target.value }))} data-testid="smtp-smtp-host" />
                <Input placeholder="SMTP port" value={smtp.smtp_port} onChange={(e) => setSmtp((s) => ({ ...s, smtp_port: e.target.value }))} data-testid="smtp-smtp-port" />
                <Select value={smtp.smtp_security} onValueChange={(v) => setSmtp((s) => ({ ...s, smtp_security: v }))}>
                  <SelectTrigger className="h-9 col-span-2" data-testid="smtp-security"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ssl">SSL</SelectItem><SelectItem value="starttls">STARTTLS</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={connectSmtp} disabled={smtpLoading} className="w-full" data-testid="connect-smtp-button">
              {smtpLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("connecting")}</> : <><Mail className="w-4 h-4 mr-2" />{t("connect")}</>}
            </Button>
          </div>

          <div className="relative py-1"><div className="border-t border-border" /><span className="absolute left-1/2 -translate-x-1/2 -top-2 bg-background px-2 text-[10px] text-muted-foreground uppercase">{t("orAddInApp")}</span></div>
          <div className="flex gap-2">
            <Input placeholder={t("emailAddress")} value={newAddr} onChange={(e) => setNewAddr(e.target.value)} data-testid="new-email-address" />
            <Button variant="outline" onClick={connect} data-testid="save-email-account">{t("add")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* simulate inbound dialog */}
      <Dialog open={simOpen} onOpenChange={setSimOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("simulateEmail")}</DialogTitle>
            <DialogDescription>{t("aiTriage")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={sim.account_id} onValueChange={(v) => setSim((s) => ({ ...s, account_id: v }))}>
              <SelectTrigger data-testid="sim-account"><SelectValue placeholder={t("emailAddress")} /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.address}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder={t("fromName")} value={sim.from_name} onChange={(e) => setSim((s) => ({ ...s, from_name: e.target.value }))} />
              <Input placeholder={t("fromEmail")} value={sim.from_email} onChange={(e) => setSim((s) => ({ ...s, from_email: e.target.value }))} data-testid="sim-from-email" />
            </div>
            <Input placeholder={t("subject")} value={sim.subject} onChange={(e) => setSim((s) => ({ ...s, subject: e.target.value }))} data-testid="sim-subject" />
            <Textarea rows={4} placeholder={t("message")} value={sim.body} onChange={(e) => setSim((s) => ({ ...s, body: e.target.value }))} data-testid="sim-body" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSimOpen(false)}>{t("cancel")}</Button>
            <Button onClick={runSim} disabled={simLoading || !sim.account_id || !sim.subject} data-testid="run-sim">{simLoading ? <><Sparkles className="w-4 h-4 mr-1 animate-pulse" />{t("aiTriage")}</> : <><Zap className="w-4 h-4 mr-1" />{t("simulateEmail")}</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
